import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PageHeader, useConfirm, EmptyState, StatusBadge, RelatedLinks, useFocusParam, focusElement } from '../components/ui';
import { Icon } from '../components/icons';
import { FlowStepBar, useLinkedInFlow } from '../linkedin';
import {
  ideaStage, ideaFormat, ideaVoice, ideaTeaser, ideaLinks, STAGE_ORDER, STAGE_STEPS, stageByKey, type IdeaStageKey,
} from '../linkedin/ideaLinks';
import {
  editorialPlanApi, EditorialPlan, EditorialPlanSlot, EditorialPlanSlotInput, CreateEditorialPlanRequest,
  EditorialPlanPreview, seriesFormatApi, SeriesFormat, postIdeaApi, PostIdea,
  socialPostApi, socialStapelApi, type SocialPlattform, type SocialAutoStand,
} from '../services/api';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import { SocialStapelDialog, FassungsPlaketten, AutoFassungenStand } from '../social/SocialStapel';
import { Vorschaubild } from '../components/media/Vorschaubild';
import { useVorschauJeIdee } from '../components/media/useVorschauJeIdee';
import './EditorialPlanPageNew.css';

const EMPTY_SLOT: EditorialPlanSlotInput = { scheduledDate: '', postIdeaId: '', seriesFormatId: '', notes: '' };
const EMPTY_PLAN: CreateEditorialPlanRequest = { name: '', startDate: '', endDate: '', postsPerWeek: 3, schedule: {}, postIdeaIds: [], slots: [] };

const datum = (iso: string) => new Date(iso).toLocaleDateString('de-DE');
const tagMonat = (d: Date) => d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
const uhrzeit = (iso: string) => new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
const wochentag = (iso: string) => new Date(iso).toLocaleDateString('de-DE', { weekday: 'short' });
const datumZeit = (iso: string) => new Date(iso).toLocaleString('de-DE', {
  weekday: 'short', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
});

/** Fuer <input type="datetime-local">: lokale Zeit ohne Zeitzone. */
const alsLokal = (iso: string) => {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

/** Montag 00:00 der Woche und ISO-Kalenderwoche. */
function woche(iso: string) {
  const d = new Date(iso);
  const montag = new Date(d.getFullYear(), d.getMonth(), d.getDate() - ((d.getDay() + 6) % 7));
  const donnerstag = new Date(montag.getFullYear(), montag.getMonth(), montag.getDate() + 3);
  const jahresStart = new Date(donnerstag.getFullYear(), 0, 1);
  const kw = 1 + Math.floor((donnerstag.getTime() - jahresStart.getTime()) / 86400000 / 7);
  return { key: montag.getTime(), montag, kw };
}

type PlanPhase = 'läuft' | 'geplant' | 'abgeschlossen';
function planPhase(plan: EditorialPlan, jetzt: number): PlanPhase {
  const start = new Date(plan.startDate).getTime();
  const ende = new Date(plan.endDate).getTime() + 86400000;
  if (jetzt < start) return 'geplant';
  if (jetzt >= ende) return 'abgeschlossen';
  return 'läuft';
}

/** Laufender Plan, sonst der naechste, sonst der neueste. */
function standardPlan(plans: EditorialPlan[], jetzt: number): EditorialPlan | undefined {
  return plans.find((p) => planPhase(p, jetzt) === 'läuft')
    ?? [...plans].filter((p) => planPhase(p, jetzt) === 'geplant').sort((a, b) => a.startDate.localeCompare(b.startDate))[0]
    ?? plans[0];
}

/** Hoechstzahl Ids je Abfrage GET /social-posts/vorhanden. */
const VORHANDEN_PAKET = 200;
/** Abfrageabstand, solange automatische Fassungen laufen. */
const AUTO_FASSUNGEN_INTERVALL_MS = 10_000;

const EditorialPlanPageNew: React.FC = () => {
  const { confirm } = useConfirm();
  const darfBearbeiten = useBerechtigung().darf('linkedin.bearbeiten');
  const { refresh } = useLinkedInFlow();
  const [searchParams, setSearchParams] = useSearchParams();
  const [editorialPlans, setEditorialPlans] = useState<EditorialPlan[]>([]);
  const [seriesFormats, setSeriesFormats] = useState<SeriesFormat[]>([]);
  const [postIdeas, setPostIdeas] = useState<PostIdea[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<EditorialPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hinweis, setHinweis] = useState('');

  // Plan anlegen / bearbeiten
  const [planForm, setPlanForm] = useState<'create' | 'edit' | null>(null);
  const [planData, setPlanData] = useState<CreateEditorialPlanRequest>(EMPTY_PLAN);

  // Slot anlegen / bearbeiten
  const [slotForm, setSlotForm] = useState<{ mode: 'add' } | { mode: 'edit'; slotId: string } | null>(null);
  const [slotData, setSlotData] = useState<EditorialPlanSlotInput>(EMPTY_SLOT);

  // Ansicht
  const [stageFilter, setStageFilterState] = useState<IdeaStageKey | 'all'>(() => {
    const s = searchParams.get('stand');
    return s && (STAGE_ORDER as string[]).includes(s) ? (s as IdeaStageKey) : 'all';
  });
  // Filter in die URL, damit "alle Slots ohne Text" verlinkbar ist.
  const setStageFilter = (f: IdeaStageKey | 'all') => {
    setStageFilterState(f);
    const p = new URLSearchParams(searchParams);
    if (f === 'all') p.delete('stand'); else p.set('stand', f);
    p.delete('slot');
    setSearchParams(p, { replace: true });
  };
  const [view, setView] = useState<'cards' | 'table'>('cards');
  const [showPast, setShowPast] = useState(false);

  // Automatisch aus fertigen Beitraegen
  const [autoOpen, setAutoOpen] = useState(false);
  const [autoPreview, setAutoPreview] = useState<EditorialPlanPreview | null>(null);
  const [autoStartDate, setAutoStartDate] = useState('');
  const [autoLoading, setAutoLoading] = useState(false);
  const [autoCreating, setAutoCreating] = useState(false);

  // Fassungen fuer Instagram / X / Facebook (viele Ideen auf einmal)
  const [stapelOffen, setStapelOffen] = useState(false);
  const [fassungen, setFassungen] = useState<Record<string, SocialPlattform[]>>({});

  const jetzt = Date.now();
  const focusSlotId = useFocusParam('slot', Boolean(selectedPlan));

  const loadPlanDetails = useCallback(async (planId: string) => {
    try {
      const plan = await editorialPlanApi.getById(planId, true);
      setSelectedPlan(plan);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Laden des Plans');
    }
  }, []);

  const loadData = useCallback(async (preferPlanId?: string | null, ideaId?: string | null) => {
    try {
      setLoading(true);
      const [plans, formats, ideas] = await Promise.all([
        editorialPlanApi.getAll(),
        seriesFormatApi.getAll(),
        postIdeaApi.getAll(),
      ]);
      setEditorialPlans(plans);
      setSeriesFormats(formats);
      setPostIdeas(ideas);
      // Rueckweg von Idee, Kalender & Co.: Plan und Slot der Idee suchen.
      let ziel = preferPlanId ? plans.find((p) => p.id === preferPlanId) : undefined;
      if (ideaId) {
        const treffer = plans.find((p) => p.scheduledSlots?.some((s) => s.postIdeaId === ideaId));
        const slot = treffer?.scheduledSlots?.find((s) => s.postIdeaId === ideaId);
        if (treffer && slot) {
          ziel = treffer;
          setSearchParams({ plan: treffer.id, slot: slot.id }, { replace: true });
        } else {
          setHinweis('Diese Beitragsidee ist in keinem Wochenplan eingeplant.');
        }
      }
      ziel = ziel || standardPlan(plans, Date.now());
      if (ziel) await loadPlanDetails(ziel.id);
      else setSelectedPlan(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Laden der Wochenpläne');
    } finally {
      setLoading(false);
    }
    // setSearchParams ist stabil genug; nur beim Laden gebraucht.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadPlanDetails]);

  useEffect(() => {
    loadData(searchParams.get('plan'), searchParams.get('idea'));
    // Nur beim Einstieg; danach steuert die Auswahl die URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectPlan = (planId: string) => {
    setStageFilter('all');
    setShowPast(false);
    setSlotForm(null);
    setPlanForm(null);
    setSearchParams({ plan: planId }, { replace: true });
    loadPlanDetails(planId);
  };

  const jumpToSlot = (slotId: string) => {
    if (selectedPlan) setSearchParams({ plan: selectedPlan.id, slot: slotId }, { replace: true });
    focusElement(slotId);
  };

  // --- Plan -----------------------------------------------------------------

  const openCreatePlan = () => {
    setPlanData(EMPTY_PLAN);
    setPlanForm(planForm === 'create' ? null : 'create');
  };

  const openEditPlan = () => {
    if (!selectedPlan) return;
    setPlanData({
      ...EMPTY_PLAN,
      name: selectedPlan.name,
      startDate: selectedPlan.startDate.slice(0, 10),
      endDate: selectedPlan.endDate.slice(0, 10),
      postsPerWeek: selectedPlan.postsPerWeek,
    });
    setPlanForm('edit');
  };

  const submitPlan = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      if (planForm === 'edit' && selectedPlan) {
        const { name, startDate, endDate, postsPerWeek } = planData;
        await editorialPlanApi.update(selectedPlan.id, { name, startDate, endDate, postsPerWeek });
        await loadData(selectedPlan.id);
      } else {
        const plan = await editorialPlanApi.create(planData);
        setSearchParams({ plan: plan.id }, { replace: true });
        await loadData(plan.id);
      }
      refresh();
      setPlanForm(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Speichern des Plans');
    }
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({ title: 'Wochenplan löschen?', message: 'Alle Slots dieses Plans werden entfernt.', confirmLabel: 'Löschen', tone: 'danger' });
    if (!ok) return;
    try {
      await editorialPlanApi.delete(id);
      setSelectedPlan(null);
      setSearchParams({}, { replace: true });
      await loadData();
      refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Löschen');
    }
  };

  // --- Slots ----------------------------------------------------------------

  const openAddSlot = () => {
    setSlotData(EMPTY_SLOT);
    setSlotForm(slotForm?.mode === 'add' ? null : { mode: 'add' });
  };

  const openEditSlot = (slot: EditorialPlanSlot) => {
    setSlotData({
      scheduledDate: alsLokal(slot.scheduledDate),
      postIdeaId: slot.postIdeaId || '',
      seriesFormatId: slot.seriesFormatId || '',
      notes: slot.notes || '',
    });
    setSlotForm({ mode: 'edit', slotId: slot.id });
  };

  const submitSlot = async () => {
    if (!selectedPlan || !slotForm) return;
    try {
      if (slotForm.mode === 'edit') await editorialPlanApi.updateSlot(slotForm.slotId, slotData);
      else await editorialPlanApi.addSlot(selectedPlan.id, slotData);
      await loadPlanDetails(selectedPlan.id);
      setSlotForm(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Speichern des Slots');
    }
  };

  const handleDeleteSlot = async (slotId: string) => {
    const ok = await confirm({ title: 'Slot löschen?', confirmLabel: 'Löschen', tone: 'danger' });
    if (!ok) return;
    try {
      await editorialPlanApi.deleteSlot(slotId);
      if (selectedPlan) await loadPlanDetails(selectedPlan.id);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Löschen des Slots');
    }
  };

  // --- Automatisch ----------------------------------------------------------

  const loadAutoPreview = async (startDate: string) => {
    setAutoLoading(true);
    try {
      setAutoPreview(await editorialPlanApi.previewAuto(startDate ? { startDate } : {}));
    } catch (err: any) {
      setAutoPreview(null);
      setError(err.response?.data?.error || 'Fehler beim Erstellen der Vorschau');
    } finally {
      setAutoLoading(false);
    }
  };

  const openAuto = () => {
    setError('');
    setAutoOpen(true);
    loadAutoPreview(autoStartDate);
  };

  const closeAuto = () => {
    setAutoOpen(false);
    setAutoPreview(null);
  };

  const handleConfirmAuto = async () => {
    if (!autoPreview) return;
    setAutoCreating(true);
    setError('');
    try {
      const plan = await editorialPlanApi.autoGenerate(autoStartDate ? { startDate: autoStartDate } : {});
      closeAuto();
      setAutoStartDate('');
      setSearchParams({ plan: plan.id }, { replace: true });
      await loadData(plan.id);
      refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim automatischen Erstellen');
    } finally {
      setAutoCreating(false);
    }
  };

  // --- Abgeleitet -----------------------------------------------------------

  const formatName = useMemo(() => new Map(seriesFormats.map((f) => [f.id, f.name])), [seriesFormats]);

  const slots = useMemo(
    () => [...(selectedPlan?.scheduledSlots ?? [])].sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate)),
    [selectedPlan],
  );

  // Hauptmedium je Idee im Plan fuer das Vorschaubild im Slot.
  const vorschauJeIdee = useVorschauJeIdee(slots.map((s) => s.postIdeaId));

  // Welche Ideen schon Fassungen fuer Instagram / X / Facebook haben (Plaketten im Slot).
  const ideenSchluessel = useMemo(
    () => Array.from(new Set(slots.map((s) => s.postIdeaId).filter((i): i is string => Boolean(i)))).sort().join(','),
    [slots],
  );
  const ladeFassungen = useCallback(async () => {
    const ids = ideenSchluessel ? ideenSchluessel.split(',') : [];
    if (ids.length === 0) {
      setFassungen({});
      return;
    }
    try {
      const karte = new Map<string, SocialPlattform[]>();
      for (let i = 0; i < ids.length; i += VORHANDEN_PAKET) {
        const teil = await socialPostApi.vorhanden(ids.slice(i, i + VORHANDEN_PAKET));
        if (teil && typeof teil === 'object') {
          for (const [id, plattformen] of Object.entries(teil)) if (Array.isArray(plattformen)) karte.set(id, plattformen);
        }
      }
      setFassungen(Object.fromEntries(karte));
    } catch {
      // Ohne Plaketten weiter; der Plan selbst ist geladen.
    }
  }, [ideenSchluessel]);
  useEffect(() => { ladeFassungen(); }, [ladeFassungen]);

  // Automatische Fassungen (nach LinkedIn-Speichern bzw. nach dem Start):
  // Stand laden; solange sie laufen, alle 10 s Stand und Plaketten
  // auffrischen, nach dem Ende ein letztes Mal.
  const [autoFassungen, setAutoFassungen] = useState<SocialAutoStand | null>(null);
  const autoLief = useRef(false);
  useEffect(() => {
    if (!ideenSchluessel) return undefined;
    let aktiv = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const abfragen = async (erstes: boolean) => {
      try {
        const zustand = await socialStapelApi.zustand();
        if (!aktiv) return;
        const auto = zustand?.auto ?? null;
        setAutoFassungen(auto);
        if (auto?.laufend) {
          if (!erstes) ladeFassungen();
          autoLief.current = true;
          timer = setTimeout(() => { abfragen(false); }, AUTO_FASSUNGEN_INTERVALL_MS);
        } else if (autoLief.current) {
          autoLief.current = false;
          ladeFassungen();
        }
      } catch {
        // Ohne Stand weiter (etwa ohne Leserecht fuer LinkedIn).
      }
    };
    abfragen(true);
    return () => {
      aktiv = false;
      if (timer) clearTimeout(timer);
    };
  }, [ideenSchluessel, ladeFassungen]);
  const fassungenVon = (ideaId?: string | null): SocialPlattform[] | undefined =>
    (ideaId && Object.prototype.hasOwnProperty.call(fassungen, ideaId) ? fassungen[ideaId] : undefined);

  const stageCounts = useMemo(() => {
    const counts = Object.fromEntries(STAGE_ORDER.map((k) => [k, 0])) as Record<IdeaStageKey, number>;
    for (const s of slots) counts[ideaStage(s.postIdea).key] += 1;
    return counts;
  }, [slots]);

  const nextSlot = useMemo(
    () => slots.find((s) => new Date(s.scheduledDate).getTime() >= jetzt && ideaStage(s.postIdea).key !== 'live'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [slots],
  );

  const sichtbar = useMemo(
    () => slots.filter((s) => stageFilter === 'all' || ideaStage(s.postIdea).key === stageFilter),
    [slots, stageFilter],
  );

  const diesWoche = woche(new Date(jetzt).toISOString()).key;
  const wochen = useMemo(() => {
    const map = new Map<number, { kw: number; montag: Date; slots: EditorialPlanSlot[] }>();
    for (const s of sichtbar) {
      const w = woche(s.scheduledDate);
      const eintrag = map.get(w.key) ?? { kw: w.kw, montag: w.montag, slots: [] };
      eintrag.slots.push(s);
      map.set(w.key, eintrag);
    }
    return [...map.entries()].sort(([a], [b]) => a - b).map(([key, w]) => ({ key, ...w }));
  }, [sichtbar]);

  const vergangen = wochen.filter((w) => w.key < diesWoche);
  // Vergangene Wochen nur einklappen, wenn danach noch etwas kommt.
  const pastCollapsible = vergangen.length > 0 && vergangen.length < wochen.length;
  const focusInPast = Boolean(focusSlotId && vergangen.some((w) => w.slots.some((s) => s.id === focusSlotId)));
  const sichtbareWochen = pastCollapsible && !showPast && !focusInPast ? wochen.filter((w) => w.key >= diesWoche) : wochen;

  const ideasInPlan = useMemo(() => new Set(slots.map((s) => s.postIdeaId).filter(Boolean)), [slots]);
  const sortedIdeas = useMemo(() => [...postIdeas].sort((a, b) => a.title.localeCompare(b.title, 'de')), [postIdeas]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // --- Bausteine ------------------------------------------------------------

  const slotFormular = (titel: string) => (
    <div className="form-section ep-slot-form">
      <h4>{titel}</h4>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label" htmlFor="ep-slot-date">Datum &amp; Uhrzeit *</label>
          <input
            id="ep-slot-date"
            type="datetime-local"
            value={slotData.scheduledDate}
            onChange={(e) => setSlotData({ ...slotData, scheduledDate: e.target.value })}
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="ep-slot-idea">Beitragsidee</label>
          <select
            id="ep-slot-idea"
            value={slotData.postIdeaId || ''}
            onChange={(e) => {
              const idea = postIdeas.find((i) => i.id === e.target.value);
              setSlotData({
                ...slotData,
                postIdeaId: e.target.value,
                // Serienformat der Idee uebernehmen, solange keins gewaehlt ist.
                seriesFormatId: slotData.seriesFormatId || idea?.seriesFormatId || '',
              });
            }}
            className="form-select"
          >
            <option value="">-- Keine --</option>
            {sortedIdeas.map((idea) => (
              <option key={idea.id} value={idea.id}>
                {idea.title}{idea.topic ? ` · ${idea.topic}` : ''}{ideasInPlan.has(idea.id) && idea.id !== slotData.postIdeaId ? ' (schon im Plan)' : ''}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label" htmlFor="ep-slot-format">Serienformat</label>
          <select
            id="ep-slot-format"
            value={slotData.seriesFormatId || ''}
            onChange={(e) => setSlotData({ ...slotData, seriesFormatId: e.target.value })}
            className="form-select"
          >
            <option value="">-- Kein Format --</option>
            {seriesFormats.map((format) => (
              <option key={format.id} value={format.id}>{format.name}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="ep-slot-notes">Notizen</label>
          <textarea
            id="ep-slot-notes"
            value={slotData.notes || ''}
            onChange={(e) => setSlotData({ ...slotData, notes: e.target.value })}
            className="form-input"
            rows={2}
            placeholder="Optionale Notizen..."
          />
        </div>
      </div>
      <div className="btn-group">
        <button onClick={submitSlot} disabled={!slotData.scheduledDate} className="btn btn-success">Speichern</button>
        <button onClick={() => setSlotForm(null)} className="btn btn-secondary">Abbrechen</button>
      </div>
    </div>
  );

  const stufenLeiter = (step: number) => (
    <ol className="ep-ladder" aria-label={`Stand: ${step} von 5 Schritten`}>
      {STAGE_STEPS.map((s, i) => (
        <li key={s.key} className={i < step ? 'is-done' : undefined}>{s.label}</li>
      ))}
    </ol>
  );

  const slotKarte = (slot: EditorialPlanSlot) => {
    if (slotForm?.mode === 'edit' && slotForm.slotId === slot.id) {
      return <div key={slot.id} data-focus-id={slot.id}>{slotFormular('Slot bearbeiten')}</div>;
    }
    const idea = slot.postIdea ?? null;
    const stage = ideaStage(idea);
    const format = ideaFormat(idea);
    const voice = ideaVoice(idea);
    const teaser = ideaTeaser(idea);
    const serie = slot.seriesFormatId ? formatName.get(slot.seriesFormatId) : idea?.seriesFormat?.name;
    const vorschau = idea ? vorschauJeIdee[idea.id] : null;
    const ueberfaellig = new Date(slot.scheduledDate).getTime() < jetzt && stage.key !== 'live';
    const istNaechster = nextSlot?.id === slot.id;

    return (
      <article
        key={slot.id}
        className={`ep-slot ep-slot--${stage.key}${istNaechster ? ' ep-slot--next' : ''}${focusSlotId === slot.id ? ' ep-slot--focus' : ''}`}
        data-focus-id={slot.id}
        aria-label={`${datumZeit(slot.scheduledDate)}: ${idea?.title ?? 'ohne Beitrag'}`}
      >
        <div className="ep-slot__when">
          <span className="ep-slot__weekday">{wochentag(slot.scheduledDate)}</span>
          <span className="ep-slot__date">{tagMonat(new Date(slot.scheduledDate))}</span>
          <span className="ep-slot__time">{uhrzeit(slot.scheduledDate)}</span>
        </div>

        <div className="ep-slot__body">
          <div className="ep-slot__meta">
            <StatusBadge tone={stage.tone} title={`Nächster Schritt: ${stage.next}`}>{stage.label}</StatusBadge>
            {istNaechster && <StatusBadge tone="primary" icon="flag">Als Nächstes</StatusBadge>}
            {ueberfaellig && <StatusBadge tone="danger" icon="alert" title="Termin liegt in der Vergangenheit, Beitrag ist nicht veröffentlicht">Überfällig</StatusBadge>}
            {idea && <span className="ep-tag"><Icon name={format.icon} size={12} aria-hidden="true" />{format.label}</span>}
            {serie && (slot.seriesFormatId || idea?.seriesFormatId) && (
              <Link className="ep-tag ep-tag--link" to={`/series-formats?id=${encodeURIComponent((slot.seriesFormatId || idea?.seriesFormatId) as string)}`} title="Serienformat öffnen">
                <Icon name="layers" size={12} aria-hidden="true" />{serie}
              </Link>
            )}
            {voice && <span className="ep-tag"><Icon name="user" size={12} aria-hidden="true" />{voice}</span>}
            {idea && <FassungsPlaketten plattformen={fassungenVon(idea.id)} />}
          </div>

          {idea ? (
            <>
              <div className="mit-vorschau">
                {vorschau && (
                  <Vorschaubild
                    assetId={vorschau.assetId}
                    kind={vorschau.kind}
                    vorschau={vorschau.vorschau}
                    groesse={88}
                    alt={`Vorschau zu „${idea.title}“`}
                  />
                )}
                <div className="mit-vorschau__text ep-slot__idea">
                  <h4 className="ep-slot__title">
                    <Link to={`/post-ideas?id=${encodeURIComponent(idea.id)}`}>{idea.title}</Link>
                  </h4>
                  {idea.topic && <p className="ep-slot__topic">{idea.topic}</p>}
                  {teaser && <p className="ep-slot__teaser">{teaser}</p>}
                </div>
              </div>
              {stufenLeiter(stage.step)}
              <RelatedLinks items={ideaLinks(idea)} label={`Verknüpft mit „${idea.title}“`} />
            </>
          ) : (
            <div className="ep-slot__empty">
              <span>Diesem Slot ist noch keine Beitragsidee zugewiesen.</span>
              <button type="button" className="btn btn-sm btn-primary" onClick={() => openEditSlot(slot)}>Idee zuweisen</button>
            </div>
          )}

          {slot.notes && <p className="ep-slot__notes"><Icon name="info" size={12} aria-hidden="true" /> {slot.notes}</p>}
        </div>

        <div className="ep-slot__actions">
          <button onClick={() => openEditSlot(slot)} className="btn-icon" title="Slot bearbeiten" aria-label="Slot bearbeiten">
            <Icon name="edit" size={16} />
          </button>
          <button onClick={() => handleDeleteSlot(slot.id)} className="btn-icon btn-danger-icon" title="Slot löschen" aria-label="Slot löschen">
            <Icon name="trash" size={16} />
          </button>
        </div>
      </article>
    );
  };

  const tabelle = (
    <div className="overview-table">
      <table className="table ep-table">
        <thead>
          <tr>
            <th>Wann</th>
            <th>Beitrag</th>
            <th>Stand</th>
            <th>Format</th>
            <th>Verknüpft</th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {sichtbar.length === 0 ? (
            <tr><td colSpan={6} className="text-center text-muted">Keine Slots in dieser Auswahl</td></tr>
          ) : sichtbar.map((slot) => {
            const idea = slot.postIdea ?? null;
            const stage = ideaStage(idea);
            return (
              <tr key={slot.id} data-focus-id={slot.id}>
                <td className="ep-table__when">{datumZeit(slot.scheduledDate)}</td>
                <td>
                  {idea ? (
                    <div className="mit-vorschau">
                      {vorschauJeIdee[idea.id] && (
                        <Vorschaubild
                          assetId={vorschauJeIdee[idea.id]?.assetId}
                          kind={vorschauJeIdee[idea.id]?.kind}
                          vorschau={vorschauJeIdee[idea.id]?.vorschau}
                          groesse="s"
                          alt={`Vorschau zu „${idea.title}“`}
                        />
                      )}
                      <div className="mit-vorschau__text">
                        <Link to={`/post-ideas?id=${encodeURIComponent(idea.id)}`}><strong>{idea.title}</strong></Link>
                        {idea.topic && <div className="text-sm text-muted">{idea.topic}</div>}
                      </div>
                    </div>
                  ) : <span className="text-muted">Keine Idee zugewiesen</span>}
                  {slot.notes && <div className="text-xs text-muted mt-1">{slot.notes}</div>}
                </td>
                <td><StatusBadge tone={stage.tone} title={`Nächster Schritt: ${stage.next}`}>{stage.label}</StatusBadge></td>
                <td>
                  {idea ? ideaFormat(idea).label : '–'}
                  {idea && <div><FassungsPlaketten plattformen={fassungenVon(idea.id)} /></div>}
                </td>
                <td>{idea ? <RelatedLinks items={ideaLinks(idea)} label={`Verknüpft mit „${idea.title}“`} /> : '–'}</td>
                <td>
                  <div className="btn-group">
                    <button onClick={() => { setView('cards'); openEditSlot(slot); }} className="btn btn-sm btn-secondary">Bearbeiten</button>
                    <button onClick={() => handleDeleteSlot(slot.id)} className="btn btn-sm btn-danger">Löschen</button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const fertig = stageCounts.text + stageCounts.scheduled + stageCounts.live;

  return (
    <div className="page-container-full">
      <FlowStepBar stepId="plan" />
      <PageHeader
        eyebrow="LinkedIn-Studio · Planen & Veröffentlichen"
        title="Wochenplan (Slots)"
        subtitle="Jeder Slot zeigt Stand, Format und alles Verknüpfte. Ein Klick auf einen Chip springt direkt zu Idee, Brief, Text, Medien oder Termin."
        actions={(
          <>
            <button
              onClick={openAuto}
              className="btn btn-success"
              title="Erstellt automatisch einen Wochenplan aus allen fertiggestellten Beiträgen"
            >
              <Icon name="sparkles" size={16} aria-hidden="true" /> Aus fertigen Beiträgen erstellen
            </button>
            <button onClick={openCreatePlan} className="btn btn-primary">
              {planForm === 'create' ? 'Abbrechen' : '+ Neuer Wochenplan'}
            </button>
          </>
        )}
      />

      {autoOpen && (
        <div className="modal-overlay" onClick={closeAuto}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760 }} role="dialog" aria-modal="true" aria-labelledby="ep-auto-title">
            <div className="modal-header">
              <h2 className="modal-title" id="ep-auto-title">Wochenplan aus fertigen Beiträgen</h2>
              <button onClick={closeAuto} className="modal-close" aria-label="Schließen">×</button>
            </div>
            <div className="form-row" style={{ alignItems: 'flex-end' }}>
              <div className="form-group">
                <label className="form-label" htmlFor="ep-auto-start">Startdatum</label>
                <input
                  id="ep-auto-start"
                  type="date"
                  value={autoStartDate}
                  onChange={(e) => { setAutoStartDate(e.target.value); loadAutoPreview(e.target.value); }}
                  className="form-input"
                />
              </div>
              <p className="text-sm text-muted" style={{ marginBottom: '0.75rem' }}>
                Ohne Datum wird ab heute geplant. Die Beiträge werden auf Di/Do/Fr verteilt.
              </p>
            </div>

            {autoLoading ? (
              <p className="text-muted">Vorschau wird berechnet…</p>
            ) : !autoPreview || autoPreview.postIdeas.length === 0 ? (
              <EmptyState
                compact
                icon="calendar"
                title="Keine fertigen Beiträge"
                description="Es gibt keine fertiggestellten Beiträge, die noch eingeplant werden können."
                action={{ label: 'Zu den Beiträgen', to: '/linkedin-post-generator' }}
              />
            ) : (
              <>
                <p className="text-sm text-secondary mb-4">
                  Aus <strong>{autoPreview.postIdeas.length}</strong> Beiträgen entsteht
                  {' '}<strong>„{autoPreview.name}“</strong> ({datum(autoPreview.startDate)} – {datum(autoPreview.endDate)},
                  {' '}{autoPreview.postsPerWeek}× pro Woche). Die Beiträge werden auf „eingeplant“ gesetzt.
                </p>
                <div className="overview-table" style={{ maxHeight: 360, overflowY: 'auto' }}>
                  <table className="table">
                    <thead>
                      <tr><th>#</th><th>Datum &amp; Uhrzeit</th><th>Beitrag</th></tr>
                    </thead>
                    <tbody>
                      {autoPreview.slots.map((slot, index) => (
                        <tr key={slot.postIdeaId}>
                          <td>{index + 1}</td>
                          <td>{datumZeit(slot.scheduledDate)}</td>
                          <td>
                            <strong>{slot.postIdea?.title || 'Beitrag'}</strong>
                            {slot.postIdea?.topic && <div className="text-sm text-muted">{slot.postIdea.topic}</div>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <div className="btn-group mt-4">
              <button
                onClick={handleConfirmAuto}
                disabled={autoCreating || autoLoading || !autoPreview || autoPreview.postIdeas.length === 0}
                className="btn btn-success"
              >
                {autoCreating ? 'Wird erstellt…' : 'Bestätigen & Plan erstellen'}
              </button>
              <button onClick={closeAuto} disabled={autoCreating} className="btn btn-secondary">Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {stapelOffen && selectedPlan && (
        <SocialStapelDialog
          auto={autoFassungen}
          editorialPlanId={selectedPlan.id}
          bezeichnung={`„${selectedPlan.name}“ (${ideenSchluessel ? ideenSchluessel.split(',').length : 0} Ideen)`}
          onClose={() => setStapelOffen(false)}
          onFertig={() => { ladeFassungen(); }}
        />
      )}

      {hinweis && (
        <div className="alert alert-info" role="status">
          {hinweis}
          <button type="button" className="btn-icon" onClick={() => setHinweis('')} aria-label="Hinweis schließen" style={{ marginLeft: 'auto' }}>×</button>
        </div>
      )}

      {error && (
        <div className="alert alert-error" role="alert">
          {error}
          <button type="button" className="btn-icon" onClick={() => setError('')} aria-label="Meldung schließen" style={{ marginLeft: 'auto' }}>×</button>
        </div>
      )}

      {planForm && (
        <div className="form-section">
          <h2 className="form-section-title">{planForm === 'edit' ? 'Plan bearbeiten' : 'Neuen Wochenplan erstellen'}</h2>
          <form onSubmit={submitPlan}>
            <div className="form-group">
              <label className="form-label" htmlFor="ep-plan-name">Name *</label>
              <input
                id="ep-plan-name"
                type="text"
                value={planData.name}
                onChange={(e) => setPlanData({ ...planData, name: e.target.value })}
                required
                placeholder="z.B. März 2026 - Woche 1-4"
                className="form-input"
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="ep-plan-start">Startdatum *</label>
                <input id="ep-plan-start" type="date" value={planData.startDate} required className="form-input"
                  onChange={(e) => setPlanData({ ...planData, startDate: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="ep-plan-end">Enddatum *</label>
                <input id="ep-plan-end" type="date" value={planData.endDate} required className="form-input"
                  onChange={(e) => setPlanData({ ...planData, endDate: e.target.value })} />
              </div>
            </div>
            <div className="btn-group">
              <button type="submit" className="btn btn-success">{planForm === 'edit' ? 'Speichern' : 'Erstellen'}</button>
              <button type="button" onClick={() => setPlanForm(null)} className="btn btn-secondary">Abbrechen</button>
            </div>
          </form>
        </div>
      )}

      {/* Planauswahl */}
      <section className="mb-6" aria-label="Gespeicherte Pläne">
        <h2>Gespeicherte Pläne ({editorialPlans.length})</h2>
        {editorialPlans.length === 0 ? (
          <EmptyState
            compact
            icon="calendar"
            title="Noch kein Wochenplan"
            description={postIdeas.length === 0
              ? 'Ein Plan verteilt Beitragsideen auf Tage. Lege zuerst ein paar Ideen an, dann lohnt sich der Plan.'
              : 'Ein Plan verteilt deine Beitragsideen auf feste Tage (z. B. Di/Do/Fr).'}
            action={postIdeas.length === 0
              ? { label: 'Zu den Beitragsideen', to: '/post-ideas' }
              : { label: 'Wochenplan anlegen', onClick: () => setPlanForm('create') }}
          />
        ) : (
          <div className="ep-plans">
            {editorialPlans.map((plan) => {
              const phase = planPhase(plan, jetzt);
              const aktiv = selectedPlan?.id === plan.id;
              return (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() => selectPlan(plan.id)}
                  className={`ep-plan${aktiv ? ' is-active' : ''}`}
                  aria-pressed={aktiv}
                >
                  <span className="ep-plan__name">{plan.name}</span>
                  <span className="ep-plan__meta">
                    {datum(plan.startDate)} – {datum(plan.endDate)} · {plan.scheduledSlots?.length ?? 0} Slots
                  </span>
                  <StatusBadge tone={phase === 'läuft' ? 'success' : phase === 'geplant' ? 'info' : 'neutral'}>{phase}</StatusBadge>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {selectedPlan && (
        <div className="card">
          <div className="page-header-row">
            <div>
              <h2 className="ep-plan-title">{selectedPlan.name}</h2>
              <p className="text-sm text-secondary">
                {datum(selectedPlan.startDate)} – {datum(selectedPlan.endDate)} · {slots.length} Slots · {fertig} mit fertigem Text
              </p>
            </div>
            <div className="btn-group">
              {darfBearbeiten && ideenSchluessel && (
                <button
                  type="button"
                  onClick={() => setStapelOffen(true)}
                  className="btn btn-primary"
                  title="Erzeugt für alle Beitragsideen dieses Plans Texte für Instagram, X und Facebook"
                >
                  <Icon name="sparkles" size={14} aria-hidden="true" /> Fassungen für Instagram / X / Facebook erzeugen
                </button>
              )}
              <button onClick={openEditPlan} className="btn btn-secondary"><Icon name="edit" size={14} aria-hidden="true" /> Plan bearbeiten</button>
              <button onClick={openAddSlot} className="btn btn-success">+ Slot hinzufügen</button>
              <button onClick={() => handleDelete(selectedPlan.id)} className="btn btn-danger">Plan löschen</button>
            </div>
          </div>
          <AutoFassungenStand auto={autoFassungen} />

          {slots.length > 0 && (
            <>
              {/* Fortschritt: ein Balken, Stufen sind zugleich Filter */}
              <div className="ep-progress" aria-hidden="true">
                {STAGE_ORDER.filter((k) => stageCounts[k] > 0).map((k) => (
                  <span key={k} className={`ep-progress__seg ep-progress__seg--${k}`} style={{ flexGrow: stageCounts[k] }} />
                ))}
              </div>
              <div className="ep-filters" role="group" aria-label="Nach Stand filtern">
                <button type="button" className={`ep-filter${stageFilter === 'all' ? ' is-active' : ''}`} onClick={() => setStageFilter('all')} aria-pressed={stageFilter === 'all'}>
                  Alle <strong>{slots.length}</strong>
                </button>
                {STAGE_ORDER.filter((k) => stageCounts[k] > 0).map((k) => (
                  <button
                    key={k}
                    type="button"
                    className={`ep-filter ep-filter--${k}${stageFilter === k ? ' is-active' : ''}`}
                    onClick={() => setStageFilter(stageFilter === k ? 'all' : k)}
                    aria-pressed={stageFilter === k}
                    title={stageByKey(k).next}
                  >
                    {stageByKey(k).label} <strong>{stageCounts[k]}</strong>
                  </button>
                ))}
                <span className="ep-filters__spacer" />
                <div className="ep-view" role="group" aria-label="Ansicht">
                  <button type="button" className={view === 'cards' ? 'is-active' : undefined} onClick={() => setView('cards')} aria-pressed={view === 'cards'}>Karten</button>
                  <button type="button" className={view === 'table' ? 'is-active' : undefined} onClick={() => setView('table')} aria-pressed={view === 'table'}>Tabelle</button>
                </div>
              </div>

              {nextSlot && (
                <button type="button" className="ep-next" onClick={() => jumpToSlot(nextSlot.id)}>
                  <Icon name="flag" size={16} aria-hidden="true" />
                  <span>
                    <strong>Als Nächstes:</strong> {datumZeit(nextSlot.scheduledDate)} – {nextSlot.postIdea?.title ?? 'Slot ohne Beitrag'}
                    <span className="text-muted"> · {ideaStage(nextSlot.postIdea).next}</span>
                  </span>
                  <Icon name="arrow-right" size={16} aria-hidden="true" />
                </button>
              )}
            </>
          )}

          {slotForm?.mode === 'add' && slotFormular('Neuen Slot hinzufügen')}

          {slots.length === 0 ? (
            <EmptyState
              compact
              icon="calendar"
              title="Noch keine Slots"
              description="Lege Slots mit Datum und Uhrzeit an und weise ihnen Beitragsideen zu."
              action={{ label: '+ Slot hinzufügen', onClick: openAddSlot }}
            />
          ) : view === 'table' ? tabelle : (
            <div className="ep-weeks">
              {pastCollapsible && !focusInPast && (
                <button type="button" className="btn btn-sm btn-secondary ep-past-toggle" onClick={() => setShowPast(!showPast)}>
                  {showPast ? 'Vergangene Wochen ausblenden' : `${vergangen.length} vergangene ${vergangen.length === 1 ? 'Woche' : 'Wochen'} anzeigen`}
                </button>
              )}
              {sichtbareWochen.length === 0 && <p className="text-muted">Keine Slots in dieser Auswahl.</p>}
              {sichtbareWochen.map((w) => {
                const sonntag = new Date(w.montag.getFullYear(), w.montag.getMonth(), w.montag.getDate() + 6);
                const istDiese = w.key === diesWoche;
                return (
                  <section key={w.key} className={`ep-week${w.key < diesWoche ? ' is-past' : ''}${istDiese ? ' is-current' : ''}`} aria-label={`KW ${w.kw}`}>
                    <h3 className="ep-week__head">
                      KW {w.kw} <span>{tagMonat(w.montag)} – {tagMonat(sonntag)}</span>
                      {istDiese && <StatusBadge tone="primary">diese Woche</StatusBadge>}
                    </h3>
                    <div className="ep-week__slots">{w.slots.map(slotKarte)}</div>
                  </section>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EditorialPlanPageNew;
